import React, { useState, useEffect, useMemo } from 'react';
import { get, post, put } from '../../services/apiController';
import { TABLE_DEFAULT_LIMIT, DEBOUNCE_DELAY } from '../../util/constant';
import { Table, Modal, Button, Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { EditIcon } from '../icons';
import 'react-toastify/dist/ReactToastify.css';
import Breadcrumb from '../../components/Breadcrumb';
import PaginationInPage from '../Pagination';
import useDebounce from '../useDebounce';
import ReactSelect from '../../components/atoms/ReactSelect';
import CakeCategory from '../../components/CakeCategory';
import { useDispatch, useSelector } from 'react-redux';
import { FetchSubCategories } from '../../store/reducers/cake_sub_category_reducer';
import Select from '../../components/atoms/Select';

const CakeProduct = () => {
  const formFields = {
    name: '',
    description: '',
    categoryId: '',
    subCategoryId: '',
    price: '',
    productCode: '',
    minWeight: '',
    maxWeight: '',
    shapes: [],
    flavours: [],
  };
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [productList, setProductList] = useState([]);
  const [formDetails, setFormDetails] = useState(formFields);
  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [query, setQuery] = useState('');
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const subCategoryList = useSelector((state) => state?.cake_sub_categories?.subCategories);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(FetchSubCategories());
  }, [dispatch]);

  useMemo(() => {
    if (formDetails.categoryId) {
      const subCats = subCategoryList.filter((subCat) => subCat.parentId === +formDetails.categoryId);
      setFilteredSubCategories(subCats);
    }
  }, [formDetails.categoryId]);

  const handleShow = () => {
    setShowConfirmModel(true);
    setFormDetails(formFields);
  };

  const handleClose = () => {
    setShowConfirmModel(false);
  };

  const handleEdit = (e, id) => {
    e.preventDefault();
    get(`/cake/products/${id}`)
      .then((res) => {
        const { data } = res;
        if (data) {
          if (data.shapes && Array.isArray(data.shapes)) {
            data['shapes'] = data?.shapes?.map((item) => {
              return {
                value: item?.id,
                label: item?.name,
              };
            });
          }
          if (data.flavours && Array.isArray(data.flavours)) {
            data['flavours'] = data?.flavours?.map((item) => {
              return {
                value: item?.id,
                label: item?.name,
              };
            });
          }
          setFormDetails(data);
          setShowConfirmModel(true);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  const handleChange = (event) => {
    event.preventDefault();
    const { name, value } = event.target;
    let _formDetails = { ...formDetails, [name]: value };
    if (name === 'categoryId') {
      _formDetails = { ...formDetails, categoryId: value, subCategoryId: '' };
    }
    setFormDetails(_formDetails);
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    setFormDetails({ ...formDetails, imagesToUpload: e.target.files });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    for (const key in formDetails) {
      if (key === 'imagesToUpload' || key === 'flavours' || key === 'shapes') continue;
      formData.append(key, formDetails[key]);
    }
    if (formDetails.imagesToUpload && formDetails.imagesToUpload !== null && formDetails.imagesToUpload.length > 0) {
      for (let i = 0; i < formDetails.imagesToUpload.length; i++) {
        formData.append('images', formDetails.imagesToUpload[i]);
      }
    }
    if (formDetails.flavours && Array.isArray(formDetails.flavours) && formDetails?.flavours.length > 0) {
      formData.append('flavours', JSON.stringify(formDetails.flavours));
    }
    if (formDetails.shapes && Array.isArray(formDetails.shapes) && formDetails?.shapes.length > 0) {
      formData.append('shapes', JSON.stringify(formDetails.shapes));
    }

    if ('id' in formDetails) {
      await put(formData, '/cake/products/' + formDetails.id, false)
        .then((res) => {
          toast.success(res?.data?.message);
          setShowConfirmModel(false);
          fetchProducts();
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.response?.data?.data?.message || err.message, { autoClose: 3000 });
        });
    } else {
      await post(formData, '/cake/products', false)
        .then((res) => {
          toast.success('Product added successfully');
          setShowConfirmModel(false);
          fetchProducts();
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.response?.data?.data?.message || err.message, { autoClose: 3000 });
        });
    }
  };

  const handleImageDelete = async (productId, index) => {
    await post({ productId, index }, '/cake/products/delete-images')
      .then((res) => {
        toast.success(res?.data?.message);
        const updatedImages = formDetails.images.filter((_, imgIndex) => imgIndex !== index);
        // Update the state with the new images array
        setFormDetails({ ...formDetails, images: updatedImages });
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.response?.data?.data?.message || err.message, { autoClose: 3000 });
      });
  };

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchProducts();
      }
    },
    [query],
    DEBOUNCE_DELAY
  );

  function fetchProducts() {
    try {
      get(`/cake/products/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&query=${query}`).then((response) => {
        setProductList(response.data.rows);
        setTotalRecords(response.data.count ?? 0);
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.message || err?.message);
    }
  }

  useEffect(() => {
    fetchProducts();
  }, [currentPage]);

  return (
    <>
      <ToastContainer />
      <Breadcrumb
        navigation={[
          { link: '#', title: 'Cake Managment' },
          { link: '#', title: 'Products' },
        ]}
      />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Products | {totalRecords ?? 0}
          </h4>
          <div className="card-header-actions">
            <Form.Control
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="search"
              placeholder="Search by name, code"
              style={{ width: '250px', marginRight: '20px' }}
            />
            <button type="button" className="btn app-btn-default dark-color " onClick={handleShow}>
              Add Product
            </button>
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Sub Category</th>
                    <th>Category</th>
                    <th>Per Unit Price (₹)</th>
                    <th>Product Code</th>
                    <th>Min Weight (Kg)</th>
                    <th>Max Weight (Kg)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productList?.map((item, index) => {
                    return (
                      <tr key={item?.id}>
                        <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                        <td>{item?.name}</td>
                        <td>{item?.SubCategory?.name}</td>
                        <td>{item?.Category?.name}</td>
                        <td>{item?.price}</td>
                        <td>{item?.productCode}</td>
                        <td>{item?.minWeight}</td>
                        <td>{item?.maxWeight}</td>
                        <td className="actions">
                          <span className="edit-icon" onClick={(e) => handleEdit(e, item?.id)}>
                            <EditIcon style={{ color: 'teal' }} />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <PaginationInPage
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalRecords={totalRecords}
                TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal size="lg" show={showConfirmModel} backdrop="static" keyboard={false} className="request-modal">
        <Modal.Header>
          <Modal.Title>{'id' in formDetails ? 'Edit Product' : 'Add Product'}</Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
        </Modal.Header>
        <Modal.Body>
          <Form autoComplete="off">
            <Row>
              <Col xs={4}>
                <FloatingLabel label="Name" className="mb-3">
                  <Form.Control
                    className="input"
                    name="name"
                    onChange={handleChange}
                    value={formDetails.name}
                    type="text"
                    placeholder="Name"
                    required
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Product Code" className="mb-3">
                  <Form.Control
                    className="input"
                    name="productCode"
                    onChange={handleChange}
                    value={formDetails.productCode}
                    type="text"
                    placeholder="Product Code"
                    required
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Per Unit Price" className="mb-3">
                  <Form.Control
                    className="input"
                    name="price"
                    onChange={handleChange}
                    value={formDetails.price}
                    type="text"
                    placeholder="Per Unit Price"
                    required
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={12}>
                <FloatingLabel label="Description" className="mb-3">
                  <Form.Control
                    className="input"
                    name="description"
                    onChange={handleChange}
                    value={formDetails.description}
                    type="text"
                    placeholder="Description"
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Min Weight (Kg)" className="mb-3">
                  <Form.Control
                    className="input"
                    name="minWeight"
                    onChange={handleChange}
                    value={formDetails.minWeight}
                    type="text"
                    placeholder="Min Weight (Kg)"
                    required
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Max Weight (Kg)" className="mb-3">
                  <Form.Control
                    className="input"
                    name="maxWeight"
                    onChange={handleChange}
                    value={formDetails.maxWeight}
                    type="text"
                    placeholder="Max Weight (Kg)"
                    required
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <Form.Control
                  className="input mb-3"
                  name="imagesToUpload"
                  accept="image/jpeg, image/png, image/jpg"
                  onChange={handleFileChange}
                  type="file"
                  multiple
                  required
                  autoComplete="false"
                />
              </Col>
              <Col xs={12}>
                <div className="uploaded-images">
                  {formDetails.id && formDetails.images && Array.isArray(formDetails.images) && (
                    <div className="d-flex flex-wrap">
                      {formDetails.images.map((image, index) => (
                        <div key={index} className="image-preview p-2 position-relative" style={{ width: '100px', height: '100px' }}>
                          <img
                            src={`${process.env.REACT_APP_BASE_URL}/${image}`}
                            alt="Cake Product"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <button
                            type="button"
                            className="btn-close position-absolute"
                            aria-label="Close"
                            style={{ top: '-1px', right: '-3px', backgroundColor: 'grey' }}
                            onClick={() => handleImageDelete(formDetails.id, index)}
                          ></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="Category" className="mb-3">
                  <CakeCategory required category={formDetails.categoryId} onChange={handleChange} label={'Category'} name={'categoryId'} />
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="Sub Category" className="mb-3">
                  <Select
                    value={formDetails.subCategoryId}
                    name="subCategoryId"
                    label="Sub Category"
                    onChange={handleChange}
                    required={true}
                    data={filteredSubCategories}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '55px',
                      }),
                    }}
                  />
                </FloatingLabel>
              </Col>
              <Col className="mb-3" xs={6}>
                <Form.Label>Shapes</Form.Label>
                <ReactSelect
                  url="/cake/shapes"
                  placeholder="Select Shapes"
                  isMulti={true}
                  onChange={(value) => {
                    setFormDetails({ ...formDetails, shapes: value });
                  }}
                  value={formDetails.shapes}
                />
              </Col>
              <Col xs={6}>
                <Form.Label>Flavours</Form.Label>
                <ReactSelect
                  url="/cake/flavours"
                  placeholder="Select Flavours"
                  isMulti={true}
                  onChange={(value) => {
                    setFormDetails({ ...formDetails, flavours: value });
                  }}
                  value={formDetails.flavours}
                />
              </Col>
            </Row>
            <div className="container text-center">
              <Button onClick={handleSubmit} type="submit" className="add-request-btn mt-4 mb-3 justify-content-center">
                {'id' in formDetails ? 'Update' : 'Add'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CakeProduct;
